import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardBodyWrapper,
  CardTitle,
  ColumnWrapper,
  FormFieldCheckbox,
  FormFieldTextInput,
  FullPageSpinner,
  IconAtSymbol,
  IconEye,
  IconLockClosed,
  IconOffEye,
  NavLinkDisplay,
} from '@prosaas/psw-styleguide';
import { AUTH_SESSION } from '@prosaas/psw-events';
import { useSubscriptions } from '@prosaas/psw-utils';
import type { IApiError } from '../utils/interfaces';
import { apiService } from '../services/api.service';
import { EMAIL_REGEX } from '../utils/regexes';
import { dashboardRoute, forgotPasswordRoute } from '../utils/routes';
import { authTestIds } from '../testIds.js';
import { useAuthTranslations } from '../hooks/useAuthTranslations';
import { useErrorMessage } from '../hooks/useErrorMapping';
import { isApiError } from '../utils/typeguards.utils';

type FormInputs = {
  email: string;
  password: string;
  rememberMe: boolean;
};

const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [apiError, setApiError] = useState<false | IApiError>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [translations, isLoadingTranslations] = useAuthTranslations();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();

  const handleSession = useCallback(
    ({ sessionId: newSessionId = null }) => {
      if (newSessionId != sessionId) {
        setSessionId(newSessionId);
        navigate(dashboardRoute());
      }
    },
    [sessionId],
  );

  useSubscriptions([[AUTH_SESSION, handleSession]]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onSubmit = handleSubmit(async (data: FormInputs) => {
    setApiError(false);
    setIsSending(true);

    try {
      await apiService.logInWithEmail({
        email: data.email,
        password: data.password,
      });
    } catch (error: unknown) {
      if (isApiError(error)) {
        setApiError({ message: errorMessage(error.message) });
      } else {
        setApiError({ message: typeof error === 'string' ? error : String(error) });
      }
      setIsSending(false);
    }
  });

  const LOG_IN = translations.headerLogIn();
  const ENTER_YOUR_EMAIL = translations.enterYourEmail();
  const PASSWORD = translations.password();
  const INVALID_EMAIL = translations.invalidEmail();
  const EMAIL_ADDRESS = translations.emailAddress();
  const ENTER_YOUR_PASSWORD = translations.enterYourPassword();
  const REMEMBER_ME = translations.rememberMe();
  const SUBMIT_BUTTON = isSending ? translations.submitting() : translations.submit();
  const FORGOT_PASSWORD = translations.forgotPassword();

  if (isLoadingTranslations) {
    return <FullPageSpinner />;
  }

  return (
    <Card isRaised>
      <ColumnWrapper>
        <form onSubmit={onSubmit} data-test-id={authTestIds.loginForm}>
          <CardBodyWrapper>
            {isSending && <FullPageSpinner />}
            <CardTitle>{LOG_IN}</CardTitle>
            {apiError && (
              <Alert data={authTestIds.loginError} category="error">
                {apiError.message}
              </Alert>
            )}
            <ColumnWrapper gapSize="xs">
              <Controller
                defaultValue=""
                control={control}
                name={'email'}
                rules={{
                  required: ENTER_YOUR_EMAIL,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: INVALID_EMAIL,
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <FormFieldTextInput
                    placeholder={EMAIL_ADDRESS}
                    label={EMAIL_ADDRESS}
                    startIcon={<IconAtSymbol />}
                    disabled={isSending}
                    error={errors?.email?.message || null}
                    value={value}
                    onChange={onChange}
                    testId={authTestIds.loginEmail}
                  />
                )}
              />
              <Controller
                defaultValue=""
                control={control}
                name={'password'}
                rules={{
                  required: ENTER_YOUR_PASSWORD,
                }}
                render={({ field: { value, name, onChange } }) => (
                  <FormFieldTextInput
                    name={name}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    label={PASSWORD}
                    startIcon={<IconLockClosed />}
                    endIcon={
                      <>
                        {showPassword ? (
                          <IconEye onClick={() => setShowPassword(!showPassword)} />
                        ) : (
                          <IconOffEye onClick={() => setShowPassword(!showPassword)} />
                        )}
                      </>
                    }
                    disabled={isSending}
                    error={errors?.password?.message || null}
                    value={value}
                    onChange={onChange}
                    testId={authTestIds.loginPassword}
                  />
                )}
              />
              <FormFieldCheckbox
                id="rememberMe"
                label={REMEMBER_ME}
                disabled={isSending}
                data-test-id={authTestIds.loginRememberCheckbox}
              />
              <Button
                disabled={!!errors?.email?.message || !!errors?.password?.message || isSending}
                data-test-id={authTestIds.loginButton}
              >
                {SUBMIT_BUTTON}
              </Button>
            </ColumnWrapper>
          </CardBodyWrapper>
        </form>
        <NavLink data-test-id={authTestIds.forgot_password_button} to={forgotPasswordRoute()}>
          <NavLinkDisplay displayName={FORGOT_PASSWORD} />
        </NavLink>
      </ColumnWrapper>
    </Card>
  );
};

export default LoginForm;
